import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import Popup from '../../components/Popup';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faInfoCircle, faComments, faSheetPlastic } from "@fortawesome/free-solid-svg-icons";
import { getWatomsSystems } from '../../constants/constants';
import { useSearchFilter } from '../../hooks/useSearchFilter';
import SystemCard from '../../components/SystemCard';
import DenyAccessPage from '../../components/DenyAccessPage';
import WatomsNavbar from 'components/navbars/WatomsNavbar';
import { useTheme } from 'context/ThemeContext';
import ModernBackground from 'components/ui/ModernBackground';

const WatomsMainPage = () => {
    const navigate = useNavigate();
    const { userInfo } = useAuth();
    const { language } = useLanguage();
    const [notAvailable, setNotAvailable] = useState(false);
    const { darkMode } = useTheme();
    const systems = useMemo(
        () => getWatomsSystems(language, userInfo?.organization_id, userInfo),
        [language, userInfo]
    );

    const getTitle = useCallback(system => system.title, []);

    const visibleSystems = useMemo(() => {
        if (!systems) return [];
        if (userInfo.user_role === "PE Observer") {
            return systems.filter(obj => obj.id === "Professional examination");
        }
        return systems.filter(
            obj =>
                !(
                    obj.id === "Professional examination" &&
                    userInfo.code !== 3 &&
                    userInfo.code !== 1 &&
                    userInfo.code !== 100 &&
                    userInfo.user_role !== "PE Observer"
                )
        );
    }, [systems, userInfo]);

    const { search, setSearch, filteredItems: filteredSystems } =
        useSearchFilter(visibleSystems, getTitle);

    const togglePopup = (status) => setNotAvailable(status);

    const openPopup = () => {
        togglePopup(true);
    };

    if (!userInfo) navigate('/login');
    if (!userInfo?.systems.some(system => system.name === "Watoms")) return <DenyAccessPage homePage='/watoms/news' />;
    if (userInfo?.code === 1452 || userInfo?.code === 1476) return <DenyAccessPage homePage='/watoms/dashboard' />;
    if (userInfo?.code === 1475) return <DenyAccessPage homePage='/watoms/news' />;
    if (userInfo?.code === 1310) return <DenyAccessPage homePage='/wisdom/dashboard' />;

    return (
        <div className={`min-h-screen w-full font-[Cairo,sans-serif] transition-colors duration-500 ${darkMode ? 'bg-watomsBlue text-white' : 'bg-gradient-to-br from-blue-50 via-white to-purple-100 text-gray-900'} relative overflow-hidden`}>
            <ModernBackground />

            {/* Header Section */}
            <WatomsNavbar search={search} setSearch={setSearch}>
                {(userInfo?.organization_id === 13 || userInfo?.employee_role === "Manager") && <button
                    className="rounded-full w-10 h-10 flex justify-center items-center bg-white/80 hover:bg-gray-200 shadow transition-all"
                    title={language ? 'Question Bank' : 'استبيان'}
                    onClick={() => navigate('/watoms/helper')}
                >
                    <FontAwesomeIcon icon={faComments} className="text-watomsBlue text-lg" />
                </button>}
                {(userInfo?.organization_id === 13 || userInfo?.organization_id === 6) && <button
                    className="rounded-full w-10 h-10 flex justify-center items-center bg-white/80 hover:bg-gray-200 shadow transition-all"
                    title={language ? 'تقرير المراكز' : 'تقرير المراكز'}
                    onClick={() => navigate('/watoms//workshop-report')}
                >
                    <FontAwesomeIcon icon={faSheetPlastic} className="text-watomsBlue text-lg" />
                </button>}
                <button
                    className="rounded-full w-10 h-10 flex justify-center items-center bg-white/80 hover:bg-gray-200 shadow transition-all"
                    title={language ? 'My Profile' : 'الملف الشخصي'}
                    // onClick={() => navigate('/watoms/user-profile')}
                    onClick={() => setNotAvailable(true)}
                >
                    <FontAwesomeIcon icon={faUser} className="text-watomsBlue text-lg" />
                </button>
                <button
                    className="rounded-full w-10 h-10 flex justify-center items-center bg-white/80 hover:bg-gray-200 shadow transition-all"
                    title={language ? 'System Info' : 'معلومات النظام'}
                    // onClick={() => navigate('/watoms/system-info')}
                    onClick={() => setNotAvailable(true)}
                >
                    <FontAwesomeIcon icon={faInfoCircle} className="text-watomsBlue text-lg" />
                </button>
            </WatomsNavbar>

            {/* Main Content */}
            <div className="relative z-10 px-6 pb-5">
                {/* Watoms System Sub Header */}
                <div className="text-center mb-8">
                    <h2 className={`text-3xl font-bold mb-2 ${darkMode ? "text-white" : "text-watomsBlue dark:text-watomsLightBlue"}`}>
                        {language ? "Wabys Systems" : "أنظمة وابيز"}
                    </h2>
                    <p className={`text-lg ${darkMode ? "text-white" : "text-gray-600 dark:text-darkTextSecondary"}`}>
                        {language ? "Access all integrated systems" : "الوصول لجميع الأنظمة المتكاملة"}
                    </p>
                </div>

                {/* Watoms System Models Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-12 max-w-7xl mx-auto">
                    {filteredSystems
                        .slice()
                        .sort((a, b) => {
                            if (a.available === b.available) return 0;
                            return a.available ? -1 : 1;
                        })
                        .map(system => (
                            <SystemCard
                                key={system.id}
                                openPopup={() => openPopup()}
                                available={system.available}
                                path={system.path}
                                color={system.color}
                                iconType="awesome"
                                icon={system.icon}
                                title={system.title}
                                subtitle={system.subtitle}
                            // notificationStatus={system.notificationStatus}
                            // notificationNumber={system.notificationNumber}
                            />
                        ))}
                </div>
            </div>

            <Popup
                isOpen={notAvailable}
                onClose={() => togglePopup(false)}
                message={language ? "This system will be available soon" : "هذا النظام سيتم إطلاقه قريباً"}
                button={language ? "OK" : "حسناً"}
                form={false}
            />
        </div>
    );
};

export default WatomsMainPage; 